import { getDataAttributeSpec, validatePairsAndAssociations } from './attribute-specs.js';
import { encodeCanonicalGs1Value, NUMERIC_REGEX } from './check-digit.js';
import { tryDecompressEpcPathSegment } from './epc.js';
import { PATH_ONLY_QUALIFIERS, PRIMARY_KEY_SET, QUALIFIER_ORDER_BY_PRIMARY, } from './keys.js';
import { validateAttributeValue, validatePrimaryKeyValue } from './validators.js';
/**
 * Resolves the highest-precedence qualifier combination per GS1-Conformant Resolver Standard v1.2.1 §2.5.10.
 */
export function resolveQualifierCombination(qualifiers) {
    if ('21' in qualifiers)
        return '21';
    if ('235' in qualifiers)
        return '235';
    if ('22' in qualifiers && '10' in qualifiers)
        return '22_10';
    if ('10' in qualifiers)
        return '10';
    if ('22' in qualifiers)
        return '22';
    if ('8011' in qualifiers)
        return '8011';
    if ('254' in qualifiers)
        return '254';
    if ('7040' in qualifiers)
        return '7040';
    if ('8020' in qualifiers)
        return '8020';
    if ('8019' in qualifiers)
        return '8019';
    return 'BARE';
}
/**
 * Parses, validates, and normalises any uncompressed or compressed GS1 Digital Link URI.
 * Implements WP-21 against GS1 Digital Link Standard: URI Syntax v1.7.0,
 * GS1-Conformant Resolver Standard v1.2.1, and GS1 General Specifications Release 26.0.
 */
export function parseGS1DigitalLink(inputUri) {
    const trimmed = inputUri.trim();
    const schemeMatch = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\//.exec(trimmed);
    if (schemeMatch === null) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_SCHEME',
            message: 'URI must begin with http:// or https:// scheme.',
            clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.11, pp35–37',
        };
    }
    const scheme = (schemeMatch[1] ?? '').toLowerCase();
    if (scheme !== 'http' && scheme !== 'https') {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_SCHEME',
            message: `Unsupported URI scheme '${scheme}'. Only HTTP and HTTPS are permitted.`,
            clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.11, pp35–37',
        };
    }
    if (trimmed.includes('\\')) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_CHARACTER_SET',
            message: 'Backslash character is not permitted in GS1 Digital Link URIs.',
            clauseRef: 'GS1 General Specifications Release 26.0 Table 7-20, p551',
        };
    }
    let parsedUrl;
    try {
        parsedUrl = new URL(trimmed);
    }
    catch {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_SCHEME',
            message: 'Malformed URI syntax.',
            clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.1, p20',
        };
    }
    // Strip at most a single trailing slash (preserving any double trailing slashes so empty segments are rejected)
    const pathname = parsedUrl.pathname;
    const rawPath = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    if (rawPath === '/' || rawPath.length === 0) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_EMPTY_PATH',
            message: 'URI path is empty.',
            clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.11, pp35–37',
        };
    }
    const rawSegments = rawPath.slice(1).split('/');
    if (rawSegments.some((s) => s.length === 0)) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_QUALIFIER_ORDER',
            message: 'URI path contains empty segments.',
            clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.11, pp35–37',
        };
    }
    const segments = rawSegments;
    // Locate primary identification key AI segment (ignoring custom stems before it per §4.11)
    let primaryAiIndex = -1;
    for (let i = 0; i < segments.length - 1; i += 1) {
        const candidate = segments[i];
        if (candidate !== undefined && PRIMARY_KEY_SET.has(candidate)) {
            primaryAiIndex = i;
            break;
        }
    }
    // Check for compressed EPC binary path segment (/ex<base64url> or /eh<hex>) per URI Syntax §6.1.2 RE3 (p44)
    // and Resolver Standard v1.2.1 §2.3 (p14) & §5 conformance item 5 (p34)
    if (primaryAiIndex === -1) {
        const lastSeg = segments[segments.length - 1] ?? '';
        if (lastSeg.startsWith('ex') || lastSeg.startsWith('eh')) {
            const decompressed = tryDecompressEpcPathSegment(lastSeg);
            if (decompressed === null) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_DECOMPRESSION_FAILED',
                    message: 'Failed to decompress /ex or /eh EPC binary GS1 Digital Link path.',
                    clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §5–§6, pp38–44; GS1-Conformant Resolver Standard v1.2.1 §2.3, p14',
                };
            }
            const allowedOrder = QUALIFIER_ORDER_BY_PRIMARY[decompressed.ai];
            const qualSegments = allowedOrder
                .filter((k) => decompressed.qualifiers?.[k] !== undefined)
                .map((k) => `/${k}/${encodeCanonicalGs1Value(decompressed.qualifiers?.[k] ?? '')}`)
                .join('');
            const mergedQueryParts = [];
            for (const [k, v] of Object.entries(decompressed.dataAttributes ?? {})) {
                mergedQueryParts.push(`${k}=${encodeCanonicalGs1Value(v)}`);
            }
            for (const [k, v] of parsedUrl.searchParams.entries()) {
                mergedQueryParts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
            }
            const syntheticUri = `https://id.gs1.org/${decompressed.ai}/${encodeCanonicalGs1Value(decompressed.primaryKey)}${qualSegments}${mergedQueryParts.length > 0 ? `?${mergedQueryParts.join('&')}` : ''}`;
            const res = parseGS1DigitalLink(syntheticUri);
            if (!res.ok)
                return res;
            return {
                ok: true,
                value: {
                    ...res.value,
                    compressed: true,
                },
            };
        }
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_UNKNOWN_PRIMARY_KEY',
            message: 'URI path does not contain a recognised GS1 primary identification key.',
            clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23',
        };
    }
    const primaryAi = segments[primaryAiIndex];
    const rawPrimaryValue = segments[primaryAiIndex + 1];
    if (rawPrimaryValue === undefined || rawPrimaryValue.length === 0) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_KEY_LENGTH',
            message: 'Primary identification key value is missing.',
            clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23',
        };
    }
    const pkValidation = validatePrimaryKeyValue(primaryAi, rawPrimaryValue);
    if (!pkValidation.ok) {
        return pkValidation;
    }
    const canonicalPrimaryKey = pkValidation.canonicalKey;
    // Parse remaining path segments as key qualifiers pairs: /qualAI/qualVal
    const remainingSegments = segments.slice(primaryAiIndex + 2);
    if (remainingSegments.length % 2 !== 0) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_QUALIFIER_ORDER',
            message: 'Path qualifiers must appear as /<ai>/<value> pairs.',
            clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.8–4.9, pp25–26',
        };
    }
    const allowedOrder = QUALIFIER_ORDER_BY_PRIMARY[primaryAi];
    const qualifiers = {};
    const allPresentAis = new Set([primaryAi]);
    let lastOrderIdx = -1;
    for (let i = 0; i < remainingSegments.length; i += 2) {
        const qAi = remainingSegments[i] ?? '';
        const qRawVal = remainingSegments[i + 1] ?? '';
        const orderIdx = allowedOrder.indexOf(qAi);
        if (orderIdx === -1) {
            return {
                ok: false,
                errorCode: 'GS1_SYNTAX_INVALID_QUALIFIER_ORDER',
                message: `AI (${qAi}) is not a valid path qualifier for primary key (${primaryAi}).`,
                clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.8–4.9, pp25–26',
            };
        }
        if (orderIdx <= lastOrderIdx) {
            allPresentAis.add(qAi);
            const pairCheck = validatePairsAndAssociations(allPresentAis, qualifiers);
            if (!pairCheck.ok) {
                return pairCheck;
            }
            return {
                ok: false,
                errorCode: 'GS1_SYNTAX_INVALID_QUALIFIER_ORDER',
                message: `Path qualifier (${qAi}) is out of canonical sequence for primary key (${primaryAi}).`,
                clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.8–4.9, pp25–26',
            };
        }
        const qValCheck = validateAttributeValue(qAi, qRawVal);
        if (!qValCheck.ok) {
            return qValCheck;
        }
        qualifiers[qAi] = qValCheck.decoded;
        allPresentAis.add(qAi);
        lastOrderIdx = orderIdx;
    }
    // Parse query parameters: secondary primary keys (§4.10), GS1 data attributes, or extension params (§4.10.1)
    const dataAttributes = {};
    const secondaryKeys = {};
    const extensionParams = {};
    for (const [paramKey, paramVal] of parsedUrl.searchParams.entries()) {
        if (PATH_ONLY_QUALIFIERS.has(paramKey) ||
            (paramKey === '10' && (primaryAi === '01' || primaryAi === '8006'))) {
            return {
                ok: false,
                errorCode: 'GS1_SYNTAX_INVALID_QUALIFIER_ORDER',
                message: `Key qualifier (${paramKey}) must appear in the URI path, not the query string.`,
                clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.8–4.9, pp25–26',
            };
        }
        if (PRIMARY_KEY_SET.has(paramKey)) {
            allPresentAis.add(paramKey);
            const secCheck = validatePrimaryKeyValue(paramKey, paramVal);
            if (!secCheck.ok) {
                return secCheck;
            }
            secondaryKeys[paramKey] = secCheck.canonicalKey;
        }
        else if (getDataAttributeSpec(paramKey) !== undefined) {
            allPresentAis.add(paramKey);
            const attrCheck = validateAttributeValue(paramKey, paramVal);
            if (!attrCheck.ok) {
                return attrCheck;
            }
            dataAttributes[paramKey] = attrCheck.decoded;
        }
        else if (NUMERIC_REGEX.test(paramKey)) {
            return {
                ok: false,
                errorCode: 'GS1_SYNTAX_UNKNOWN_PRIMARY_KEY',
                message: `All-numeric query parameter (${paramKey}) is not a recognised GS1 Application Identifier; extension keys must contain at least one non-digit character.`,
                clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.10.1, pp33–34',
            };
        }
        else {
            extensionParams[paramKey] = paramVal;
        }
    }
    // Validate invalid AI pairs (Table 4-4) and mandatory AI associations (Table 4-5)
    const assocResult = validatePairsAndAssociations(allPresentAis, qualifiers);
    if (!assocResult.ok) {
        return assocResult;
    }
    // Construct canonical https://id.gs1.org URI (§4.12, pp37–38)
    const canonicalQualPath = allowedOrder
        .filter((qAi) => qualifiers[qAi] !== undefined)
        .map((qAi) => `/${qAi}/${encodeCanonicalGs1Value(qualifiers[qAi] ?? '')}`)
        .join('');
    const canonicalQueryParts = [];
    for (const secAi of Object.keys(secondaryKeys).sort()) {
        canonicalQueryParts.push(`${secAi}=${encodeCanonicalGs1Value(secondaryKeys[secAi] ?? '')}`);
    }
    for (const attrAi of Object.keys(dataAttributes).sort()) {
        canonicalQueryParts.push(`${attrAi}=${encodeCanonicalGs1Value(dataAttributes[attrAi] ?? '')}`);
    }
    const canonicalQuery = canonicalQueryParts.length > 0 ? `?${canonicalQueryParts.join('&')}` : '';
    const canonicalUri = `https://id.gs1.org/${primaryAi}/${encodeCanonicalGs1Value(canonicalPrimaryKey)}${canonicalQualPath}${canonicalQuery}`;
    return {
        ok: true,
        value: {
            ai: primaryAi,
            primaryKey: canonicalPrimaryKey,
            identifierKey: `${primaryAi}_${canonicalPrimaryKey}`,
            canonicalUri,
            qualifiers,
            resolverCombination: resolveQualifierCombination(qualifiers),
            dataAttributes,
            secondaryKeys,
            extensionParams,
            compressed: false,
        },
    };
}
//# sourceMappingURL=parse.js.map